import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useThree } from '@react-three/fiber'
import styled from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'

// Import the about image
// import aboutImage from '/about.png'


// Styled components for the About section
const AboutContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 10000; /* Increased z-index to ensure it's above everything */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  overflow-y: auto;
  color: white;
  cursor: pointer; /* Show that it's clickable */
  pointer-events: auto !important; /* Force pointer events to be enabled */
  @media (max-width: 768px) {
    padding: 1rem;
  }
`

const AboutContent = styled(motion.div)`
  max-width: 1200px;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: default; /* Reset cursor for content */
  pointer-events: auto; /* Ensure pointer events work on content */
`

const AboutImage = styled.img`
  width: 100%;
  height: auto;
  max-height: 90vh;
  object-fit: contain;
  margin-bottom: 2rem;
  pointer-events: auto; /* Ensure pointer events work on image */
`

const CloseButton = styled(motion.button)`
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  z-index: 10001; /* Ensure it's above everything */
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto; /* Ensure pointer events work on button */
`

// The About component
export const About = ({ isAboutVisible, onClose }) => {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  })

  // Handle click on container to close modal
  const handleContainerClick = useCallback((e) => {
    // Check if the click is on the backdrop
    if (e.target === e.currentTarget) {
      console.log('Backdrop clicked, closing modal');
      onClose();
      e.stopPropagation(); // Stop event propagation
    }
  }, [onClose]);

  // Handle close button click
  const handleCloseClick = useCallback((e) => {
    console.log('Close button clicked');
    onClose();
    e.stopPropagation(); // Stop event propagation
  }, [onClose]);

  // Handle Escape key press
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      console.log('Escape key pressed, closing modal');
      onClose();
    }
  }, [onClose]);

  // Update dimensions when window resizes
  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Add keyboard event listener when modal is open
  useEffect(() => {
    if (isAboutVisible) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isAboutVisible, handleKeyDown]);

  // Add a debug log to check when the component renders
  useEffect(() => {
    console.log('About component visible:', isAboutVisible);
  }, [isAboutVisible]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { 
      opacity: 0,
      transition: { 
        duration: 0.2,
        when: "afterChildren",
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    }
  }

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1,
      y: 0,
      transition: { 
        duration: 0.3
      }
    },
    exit: { 
      opacity: 0,
      y: 20,
      transition: { 
        duration: 0.2
      }
    }
  }

  // If not visible, don't render anything
  if (!isAboutVisible) return null;

  return (
    <AboutContainer
      className="about-container-backdrop"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={containerVariants}
      onClick={handleContainerClick}
    >
      <AboutContent 
        variants={contentVariants}
        onClick={(e) => e.stopPropagation()} // Prevent clicks on content from closing the modal
      >
        <CloseButton
          onClick={handleCloseClick}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <CloseIcon fontSize="large" />
        </CloseButton>
        
        <AboutImage 
          src={aboutImage} 
          alt="Glitch Studio - About" 
        />
      </AboutContent>
    </AboutContainer>
  )
} 